import React, { useState, useEffect } from "react";
import "./RoleManagement.css";
import {
  subscribeAdminUsers,
  createAdminUser,
  updateAdminUser,
  suspendAdminUser,
} from "../../utils/adminAuth";
import {
  getAllRoles,
  getOrgRoles,
  getVendorRoles,
  canDelete,
} from "../../utils/rbac";
import firebase from "../../firebase";

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => (
  <span className={`rm_status rm_status--${status}`}>{status}</span>
);

const RoleBadge = ({ role }) => {
  const isVendor = role === "salon_super_admin" || role === "salon_admin";
  return (
    <span className={`rm_role_badge ${isVendor ? "rm_role_badge--vendor" : "rm_role_badge--org"}`}>
      {getAllRoles().find((r) => r.id === role)?.name || role}
    </span>
  );
};

// ── Empty form state ──────────────────────────────────────────────────────────
const EMPTY_FORM = {
  uid: "",
  email: "",
  name: "",
  role: "admin",
  vendorId: "",
  vendorName: "",
};

// ── Main component ────────────────────────────────────────────────────────────
const RoleManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // uid of admin being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // ── Subscribe to adminUsers collection ──────────────────────────────────────
  useEffect(() => {
    const unsubscribe = subscribeAdminUsers((data) => {
      setAdmins(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ── Derived state ────────────────────────────────────────────────────────────
  const isVendorRole = form.role === "salon_super_admin" || form.role === "salon_admin";

  const filteredAdmins = admins.filter((a) => {
    const matchSearch =
      a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = filterRole === "all" || a.role === filterRole;
    return matchSearch && matchRole;
  });

  // ── Form helpers ─────────────────────────────────────────────────────────────
  const openAddForm = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowForm(true);
  };

  const openEditForm = (admin) => {
    setEditTarget(admin.id);
    setForm({
      uid: admin.uid || admin.id,
      email: admin.email || "",
      name: admin.name || "",
      role: admin.role || "admin",
      vendorId: admin.vendorId || "",
      vendorName: admin.vendorName || "",
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ── Form submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);

    try {
      const isVendor = form.role === "salon_super_admin" || form.role === "salon_admin";

      if (isVendor && !form.vendorId.trim()) {
        setFormError("Vendor ID is required for vendor roles.");
        setFormLoading(false);
        return;
      }

      if (editTarget) {
        // ── Edit existing admin ──────────────────────────────────────────────
        await updateAdminUser(editTarget, {
          name: form.name,
          role: form.role,
          vendorId: isVendor ? form.vendorId.trim() : null,
          vendorName: isVendor ? form.vendorName.trim() : null,
        });
      } else {
        // ── Create new admin ─────────────────────────────────────────────────
        if (!form.uid.trim()) {
          setFormError("Firebase Auth UID is required.");
          setFormLoading(false);
          return;
        }
        await createAdminUser({
          uid: form.uid.trim(),
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role,
          vendorId: isVendor ? form.vendorId.trim() : null,
          vendorName: isVendor ? form.vendorName.trim() : null,
        });
      }

      closeForm();
    } catch (err) {
      setFormError(err.message || "Something went wrong. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  // ── Suspend ───────────────────────────────────────────────────────────────────
  const handleSuspend = async (uid) => {
    if (!window.confirm("Suspend this admin? They will lose access immediately.")) return;
    await suspendAdminUser(uid);
  };

  const handleReactivate = async (uid) => {
    await updateAdminUser(uid, { status: "active" });
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="rm_page">
      <div className="rm_header">
        <div>
          <h2 className="rm_title">Role Management</h2>
          <p className="rm_subtitle">
            Manage admin accounts and assign roles. Vendor roles require a Vendor ID.
          </p>
        </div>
        <button className="rm_btn rm_btn--primary" onClick={openAddForm}>
          <i className="fa fa-plus"></i> Add Admin
        </button>
      </div>

      {/* ── Summary cards ─────────────────────────────────────────────────── */}
      <div className="rm_summary">
        {getAllRoles().map((r) => (
          <div key={r.id} className={`rm_card rm_card--${r.isVendorRole ? "vendor" : "org"}`}>
            <div className="rm_card_count">
              {admins.filter((a) => a.role === r.id && a.status === "active").length}
            </div>
            <div className="rm_card_label">{r.name}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div className="rm_filters">
        <input
          className="rm_search"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="rm_select"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
        >
          <option value="all">All Roles</option>
          {getAllRoles().map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="rm_loading"><i className="fa fa-spinner fa-spin"></i> Loading...</div>
      ) : (
        <div className="rm_table_wrapper">
          <table className="rm_table">
            <thead>
              <tr>
                <th>Name / Email</th>
                <th>Role</th>
                <th>Vendor</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.length === 0 ? (
                <tr>
                  <td colSpan={5} className="rm_empty">No admin accounts found.</td>
                </tr>
              ) : (
                filteredAdmins.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      <div className="rm_user_name">{admin.name || "—"}</div>
                      <div className="rm_user_email">{admin.email}</div>
                    </td>
                    <td><RoleBadge role={admin.role} /></td>
                    <td>
                      {admin.vendorId ? (
                        <span className="rm_vendor_cell">
                          <i className="fa fa-store"></i> {admin.vendorName || admin.vendorId}
                        </span>
                      ) : "—"}
                    </td>
                    <td><StatusBadge status={admin.status || "active"} /></td>
                    <td>
                      <div className="rm_actions">
                        <button
                          className="rm_btn rm_btn--sm rm_btn--edit"
                          onClick={() => openEditForm(admin)}
                          title="Edit role"
                        >
                          <i className="fa fa-pencil"></i>
                        </button>
                        {admin.status === "active" ? (
                          <button
                            className="rm_btn rm_btn--sm rm_btn--danger"
                            onClick={() => handleSuspend(admin.id)}
                            title="Suspend"
                          >
                            <i className="fa fa-ban"></i>
                          </button>
                        ) : (
                          <button
                            className="rm_btn rm_btn--sm rm_btn--success"
                            onClick={() => handleReactivate(admin.id)}
                            title="Reactivate"
                          >
                            <i className="fa fa-check"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add / Edit Form Modal ──────────────────────────────────────────── */}
      {showForm && (
        <div className="rm_modal_overlay" onClick={closeForm}>
          <div className="rm_modal" onClick={(e) => e.stopPropagation()}>
            <div className="rm_modal_header">
              <h3>{editTarget ? "Edit Admin Role" : "Add New Admin"}</h3>
              <button className="rm_modal_close" onClick={closeForm}>
                <i className="fa fa-times"></i>
              </button>
            </div>

            <form className="rm_form" onSubmit={handleSubmit}>
              {!editTarget && (
                <>
                  <div className="rm_form_group">
                    <label>Firebase Auth UID *</label>
                    <input
                      name="uid"
                      value={form.uid}
                      onChange={handleFormChange}
                      placeholder="Paste the Firebase Auth UID"
                      required
                    />
                    <small>
                      Found in Firebase Console → Authentication → Users
                    </small>
                  </div>
                  <div className="rm_form_group">
                    <label>Email *</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleFormChange}
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                </>
              )}

              <div className="rm_form_group">
                <label>Display Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  placeholder="Full name"
                />
              </div>

              <div className="rm_form_group">
                <label>Role *</label>
                <select name="role" value={form.role} onChange={handleFormChange} required>
                  <optgroup label="Org Roles">
                    {getOrgRoles().map((r) => (
                      <option key={r.id} value={r.id}>{r.name} — {r.description}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Vendor Roles">
                    {getVendorRoles().map((r) => (
                      <option key={r.id} value={r.id}>{r.name} — {r.description}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Vendor fields — only shown for vendor roles */}
              {isVendorRole && (
                <div className="rm_vendor_section">
                  <div className="rm_vendor_section_label">
                    <i className="fa fa-store"></i> Vendor Details
                  </div>
                  <div className="rm_form_group">
                    <label>Vendor ID * <small>(Firestore document ID of the vendor)</small></label>
                    <input
                      name="vendorId"
                      value={form.vendorId}
                      onChange={handleFormChange}
                      placeholder="e.g. abc123xyz"
                    />
                  </div>
                  <div className="rm_form_group">
                    <label>Vendor / Salon Name</label>
                    <input
                      name="vendorName"
                      value={form.vendorName}
                      onChange={handleFormChange}
                      placeholder="e.g. The Glamour Studio"
                    />
                  </div>
                </div>
              )}

              {formError && <p className="rm_form_error">{formError}</p>}

              <div className="rm_form_actions">
                <button
                  type="button"
                  className="rm_btn rm_btn--secondary"
                  onClick={closeForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rm_btn rm_btn--primary"
                  disabled={formLoading}
                >
                  {formLoading
                    ? <><i className="fa fa-spinner fa-spin"></i> Saving...</>
                    : editTarget ? "Save Changes" : "Create Admin"
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
